import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, FileText, Loader2, Check, ArrowLeft, Plus, Trash2, FileInput } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { aiAPI, jobsAPI, quotesAPI, documentsAPI } from '../services/api';
import { DatePicker } from '../components/DatePicker';

const UNITS = ['Item', 'm2', 'lm', 'pc', 'ps', 'hour', 'day', 'sqm', 'm', 'kg', 'L', 'set', 'lot', 'allow'];
const TRADES = ['General', 'Plumber', 'Electrician', 'Carpenter', 'Painter', 'Tiler', 'Roofer', 'Plasterer', 'Bricklayer', 'Landscaper', 'HVAC', 'Glazier', 'Flooring', 'Demolition', 'Concrete'];

const flattenToRows = (sections) => {
  const rows = [];
  (sections || []).forEach(section => {
    (section.items || []).forEach(item => {
      rows.push({
        section_name: section.section_name || section.section_type || 'Imported',
        description: item.description || '',
        quantity: item.quantity || 1,
        unit: item.unit || 'Item',
        unit_price: item.unit_price || 0,
        trade_type: item.trade_type || 'General',
      });
    });
  });
  return rows;
};

const groupBySection = (rows) => {
  const map = new Map();
  rows.forEach(r => {
    const key = (r.section_name || 'Imported').trim() || 'Imported';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({
      description: r.description,
      quantity: parseFloat(r.quantity) || 0,
      unit: r.unit,
      unit_price: parseFloat(r.unit_price) || 0,
      trade_type: r.trade_type,
    });
  });
  return Array.from(map.entries()).map(([section_name, items]) => ({
    section_type: 'imported',
    section_name,
    items,
  }));
};

export default function ImportQuotePDFPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileRef = useRef(null);

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(searchParams.get('job_id') || '');
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    jobsAPI.getAll().then(r => setJobs(r.data || [])).catch(() => setJobs([]));
  }, []);

  const totals = (() => {
    const subtotal = rows.reduce((s, r) => s + (parseFloat(r.quantity || 0) * parseFloat(r.unit_price || 0)), 0);
    const tax = subtotal * 0.1;
    return { subtotal: +subtotal.toFixed(2), tax: +tax.toFixed(2), total: +(subtotal + tax).toFixed(2) };
  })();

  const handleExtract = async () => {
    if (!file) { toast.error('Please upload a PDF'); return; }
    if (!jobId) { toast.error('Please select a destination job'); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('job_id', jobId);
      const { data } = await aiAPI.importQuotePdf(fd);
      if (data.status === 'partial') {
        toast.warning('AI could only partially parse the PDF — please review carefully');
      } else {
        toast.success('Quote extracted — please review');
      }
      const sections = data.sections?.length ? data.sections : (data.items?.length ? [{ section_type: 'imported', section_name: 'Imported from PDF', items: data.items }] : []);
      setRows(flattenToRows(sections));
      setNotes(data.notes || '');
      setClientName(data.client_name || '');
      setClientEmail(data.client_email || '');
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to extract PDF');
    } finally {
      setExtracting(false);
    }
  };

  const updateRow = (idx, key, val) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const addRow = () => setRows(rs => [...rs, { section_name: rs[rs.length - 1]?.section_name || 'Imported', description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General' }]);
  const removeRow = (idx) => setRows(rs => rs.filter((_, i) => i !== idx));

  const handleCommit = async () => {
    if (!rows.length) { toast.error('At least one line item is required'); return; }
    setSaving(true);
    try {
      const sections = groupBySection(rows);
      const payload = {
        job_id: jobId,
        client_name: clientName || jobs.find(j => j.id === jobId)?.client_name || 'Client',
        client_email: clientEmail || jobs.find(j => j.id === jobId)?.client_email || null,
        sections,
        items: sections.flatMap(s => s.items),
        notes,
        valid_until: validUntil || null,
      };
      const { data: quote } = await quotesAPI.create(payload);

      // Attach the original PDF to the job's Documents tab
      let documentId = null;
      try {
        const docFd = new FormData();
        docFd.append('file', file);
        docFd.append('document_type', 'quotes_variations');
        docFd.append('scope', 'job');
        docFd.append('linked_entity_type', 'quote');
        docFd.append('linked_entity_id', quote.id);
        docFd.append('description', `Source PDF for quote ${quote.quote_number}`);
        const docResp = await documentsAPI.upload(docFd);
        documentId = docResp.data?.id;
      } catch (docErr) {
        // non-fatal — quote is already created
        console.warn('PDF attachment failed:', docErr);
      }

      setResult({ quote, documentId });
      setStep(3);
      toast.success(`Quote ${quote.quote_number} created!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create quote');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setStep(1); setFile(null); setRows([]); setResult(null); setNotes(''); setClientName(''); setClientEmail(''); };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="import-quote-pdf-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(jobId ? `/jobs/${jobId}` : '/quotes')} data-testid="iq-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileInput className="h-7 w-7 text-primary" />
            Import Quote from PDF
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload a quote PDF — AI extracts the sections and line items, you review and adjust, and a draft Quote is created.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-4 text-sm">
        {['Upload PDF', 'Review & Edit', 'Done'].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center border ${step > i ? 'bg-green-500/20 border-green-500 text-green-400' : step === i + 1 ? 'bg-primary/20 border-primary text-primary' : 'border-border text-muted-foreground'}`}>
              {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={step === i + 1 ? 'font-medium' : 'text-muted-foreground'}>{label}</span>
            {i < 2 && <span className="text-muted-foreground mx-2">→</span>}
          </div>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <Card className="bg-card border-border">
          <CardHeader><CardTitle>Upload the Quote PDF</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Destination Job *</Label>
              <Select value={jobId} onValueChange={setJobId}>
                <SelectTrigger className="bg-input border-border" data-testid="iq-job-select"><SelectValue placeholder="Select a job to attach the quote to" /></SelectTrigger>
                <SelectContent>
                  {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title} — {j.client_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-border rounded-sm p-16 text-center cursor-pointer hover:border-primary transition-colors"
              data-testid="iq-dropzone"
            >
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              {file ? (
                <div>
                  <FileText className="h-5 w-5 inline mr-2 text-primary" />
                  <span className="font-medium">{file.name}</span>
                  <p className="text-xs text-muted-foreground mt-2">Click to change</p>
                </div>
              ) : (
                <div>
                  <p className="text-lg font-medium">Drop your quote PDF here or click to browse</p>
                  <p className="text-sm text-muted-foreground mt-1">PDF files only</p>
                </div>
              )}
              <input type="file" ref={fileRef} accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="iq-file-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/quotes')} data-testid="iq-cancel-btn">Cancel</Button>
              <Button onClick={handleExtract} disabled={!file || !jobId || extracting} data-testid="iq-extract-btn">
                {extracting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Extracting…</> : 'Extract with AI'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle>Quote Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Client Name</Label><Input value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="iq-client-name" /></div>
              <div><Label>Client Email</Label><Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} /></div>
              <div><Label>Valid Until</Label><DatePicker value={validUntil} onChange={(d) => setValidUntil(d)} placeholder="Valid until" /></div>
              <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Line Items ({rows.length})</CardTitle>
              <Button size="sm" variant="outline" onClick={addRow} data-testid="iq-add-row-btn"><Plus className="h-4 w-4 mr-1" />Add Row</Button>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No items extracted. Add rows manually.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-left py-2 pr-2 w-40">Section</th>
                        <th className="text-left py-2 pr-2">Description</th>
                        <th className="text-right py-2 px-2 w-24">Qty</th>
                        <th className="text-left py-2 px-2 w-28">Unit</th>
                        <th className="text-right py-2 px-2 w-32">Unit Price</th>
                        <th className="text-left py-2 px-2 w-32">Trade</th>
                        <th className="text-right py-2 px-2 w-28">Total</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => {
                        const qty = parseFloat(row.quantity || 0);
                        const price = parseFloat(row.unit_price || 0);
                        return (
                          <tr key={idx} className="border-b border-border/50">
                            <td className="py-1 pr-2"><Input value={row.section_name} onChange={(e) => updateRow(idx, 'section_name', e.target.value)} /></td>
                            <td className="py-1 pr-2"><Input value={row.description} onChange={(e) => updateRow(idx, 'description', e.target.value)} data-testid={`iq-row-desc-${idx}`} /></td>
                            <td className="py-1 px-2"><Input type="number" step="0.01" value={row.quantity} onChange={(e) => updateRow(idx, 'quantity', e.target.value)} className="text-right" /></td>
                            <td className="py-1 px-2">
                              <select value={row.unit} onChange={(e) => updateRow(idx, 'unit', e.target.value)} className="w-full h-9 bg-input border border-border rounded-sm px-2 text-sm">
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="py-1 px-2"><Input type="number" step="0.01" value={row.unit_price} onChange={(e) => updateRow(idx, 'unit_price', e.target.value)} className="text-right" /></td>
                            <td className="py-1 px-2">
                              <select value={row.trade_type} onChange={(e) => updateRow(idx, 'trade_type', e.target.value)} className="w-full h-9 bg-input border border-border rounded-sm px-2 text-sm">
                                {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                            <td className="py-1 px-2 text-right font-medium">${(qty * price).toFixed(2)}</td>
                            <td className="py-1 px-2"><Button size="icon" variant="ghost" onClick={() => removeRow(idx)} data-testid={`iq-remove-row-${idx}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="text-sm"><td colSpan={6} className="py-2 text-right">Subtotal:</td><td className="py-2 px-2 text-right font-medium">${totals.subtotal.toFixed(2)}</td><td></td></tr>
                      <tr className="text-sm"><td colSpan={6} className="py-1 text-right">GST (10%):</td><td className="py-1 px-2 text-right font-medium">${totals.tax.toFixed(2)}</td><td></td></tr>
                      <tr className="text-base"><td colSpan={6} className="py-2 text-right font-bold">Total:</td><td className="py-2 px-2 text-right font-bold text-primary">${totals.total.toFixed(2)}</td><td></td></tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="iq-back-step-btn"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
            <Button onClick={handleCommit} disabled={saving} data-testid="iq-commit-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : <><Check className="h-4 w-4 mr-2" />Create Quote</>}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && result && (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold" data-testid="iq-success-heading">Quote Created!</h2>
            <div className="space-y-1 text-muted-foreground">
              <p><strong className="text-foreground">Quote Number:</strong> {result.quote.quote_number}</p>
              <p><strong className="text-foreground">Total:</strong> ${result.quote.total?.toFixed(2)}</p>
              {result.documentId && (
                <p className="text-sm flex items-center justify-center gap-1.5 text-green-400">
                  <FileText className="h-4 w-4" />Source PDF attached to job's Documents tab
                </p>
              )}
            </div>
            <div className="flex gap-2 justify-center pt-4">
              <Button variant="outline" onClick={reset} data-testid="iq-import-another-btn">Import Another</Button>
              <Button onClick={() => navigate(`/jobs/${jobId}`)} data-testid="iq-view-job-btn">View Job</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
